import {
  CustomFieldResourceType,
  customFieldResourceTypeLabels,
  CustomFieldType,
} from "@repo/app-types/entities/customField";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { type FC, useMemo } from "react";

// Support contact, interview, and customer interaction (grading) resource types
export type UserCustomFieldSupportedResourceType =
  | typeof CustomFieldResourceType.CONTACT
  | typeof CustomFieldResourceType.INTERVIEW
  | typeof CustomFieldResourceType.GRADING;

const contactResourceTypeOption: SimpleSelectOption<UserCustomFieldSupportedResourceType> =
  {
    label: customFieldResourceTypeLabels[CustomFieldResourceType.CONTACT],
    value: CustomFieldResourceType.CONTACT,
  };

const interviewResourceTypeOption: SimpleSelectOption<UserCustomFieldSupportedResourceType> =
  {
    label: customFieldResourceTypeLabels[CustomFieldResourceType.INTERVIEW],
    value: CustomFieldResourceType.INTERVIEW,
  };

const customerInteractionResourceTypeOption: SimpleSelectOption<UserCustomFieldSupportedResourceType> =
  {
    label: customFieldResourceTypeLabels[CustomFieldResourceType.GRADING],
    value: CustomFieldResourceType.GRADING,
  };

const fieldTypeOptions: readonly SimpleSelectOption<CustomFieldType>[] = [
  {
    label: "Text",
    value: CustomFieldType.Text,
  },
  {
    label: "Date",
    value: CustomFieldType.Date,
  },
  {
    label: "Date and time",
    value: CustomFieldType.DateAndTime,
  },
  {
    label: "Yes/No",
    value: CustomFieldType.Boolean,
  },
  {
    label: "Number",
    value: CustomFieldType.Number,
  },
] as const;

export interface UserCustomFieldDefinitionFormProps {
  resourceType: UserCustomFieldSupportedResourceType | null;
  displayName: string;
  fieldName: string;
  fieldType: CustomFieldType | null;
  showInterviewResourceType: boolean;
  showCustomerInteractionResourceType: boolean;
  onResourceTypeChange: (value: UserCustomFieldSupportedResourceType) => void;
  onFieldNameChange?: (value: string) => void;
  onDisplayNameChange: (value: string) => void;
  onFieldTypeChange: (value: CustomFieldType) => void;
}

export const UserCustomFieldDefinitionForm: FC<
  UserCustomFieldDefinitionFormProps
> = ({
  resourceType,
  displayName,
  fieldName,
  fieldType,
  showInterviewResourceType,
  showCustomerInteractionResourceType,
  onResourceTypeChange,
  onFieldNameChange,
  onDisplayNameChange,
  onFieldTypeChange,
}) => {
  const resourceTypeOptions = useMemo(() => {
    const options: SimpleSelectOption<UserCustomFieldSupportedResourceType>[] =
      [contactResourceTypeOption];
    if (showInterviewResourceType) {
      options.push(interviewResourceTypeOption);
    }
    if (showCustomerInteractionResourceType) {
      options.push(customerInteractionResourceTypeOption);
    }
    return options;
  }, [showInterviewResourceType, showCustomerInteractionResourceType]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-y-1">
        <Label size={Size.SMALL}>Resource</Label>
        <SimpleSelect<UserCustomFieldSupportedResourceType>
          options={resourceTypeOptions}
          value={resourceType}
          onChange={onResourceTypeChange}
          placeholder="Select resource type"
        />
        <p className="text-sm text-muted-foreground">
          Specify which type of resource this custom field belongs to.
        </p>
      </div>

      <div className="flex flex-col gap-y-1">
        <Label size={Size.SMALL}>Name</Label>
        <Input
          id="display_name"
          value={displayName}
          onChange={(e) => onDisplayNameChange(e.target.value)}
          placeholder="ATS Applicant ID"
          description="Name of the custom field. This will be displayed and can be used in message templates."
          maxLength={200}
        />
      </div>

      <div className="flex flex-col gap-y-1">
        <Label size={Size.SMALL}>Key</Label>
        <Input
          value={fieldName}
          isDisabled={onFieldNameChange === undefined}
          onChange={(event) => onFieldNameChange?.(event.target.value)}
          placeholder="ats_applicant_id"
          description="Key of the custom field. This will be used in the public API."
          maxLength={100}
        />
      </div>

      <div className="flex flex-col gap-y-1">
        <Label size={Size.SMALL}>Type</Label>
        <SimpleSelect<CustomFieldType>
          options={fieldTypeOptions}
          value={fieldType}
          onChange={onFieldTypeChange}
          placeholder="Select field type"
        />
      </div>
    </div>
  );
};
